import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  ChevronRight,
  Copy,
  Loader2,
  Megaphone,
  Pause,
  Pencil,
  Play,
  TrendingDown,
  TrendingUp,
  TriangleAlert
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Cabecalho from "./Cabecalho";
import CampanhaModal from "./CampanhaModal";
import { identidadeDaRede } from "./redes";
import {
  compararCtr,
  ctrMedio,
  dinheiro,
  numero,
  porcento,
  problemaDe,
  resultadoDe,
  situacaoDe,
  somar
} from "./anuncios-metricas";

// Campanhas de anúncio — a árvore inteira numa tela.
//
// Duas escolhas separam isto do gerenciador do Facebook, e as duas vêm de
// saber uma coisa que ele não sabe: aqui o anúncio existe para virar
// atendimento.
//
// 1. O número que manda é o custo por resultado, escolhido pelo objetivo da
//    campanha — conversa iniciada numa campanha de mensagem, lead numa de
//    lead. Lá isso está escondido dentro de "actions".
// 2. Cada CTR é comparado com a média da própria conta. "1,88%" não diz nada
//    sozinho; "1,88%, abaixo da média" diz o que fazer.

const PERIODOS = [
  { valor: 7, rotulo: "7 dias" },
  { valor: 30, rotulo: "30 dias" },
  { valor: 90, rotulo: "90 dias" }
];

const SITUACOES_FILTRO = [
  { valor: "todas", rotulo: "Todas as situações" },
  { valor: "active", rotulo: "Ativas" },
  { valor: "paused", rotulo: "Pausadas" },
  { valor: "completed", rotulo: "Concluídas" },
  { valor: "error", rotulo: "Com erro" }
];

const ORDENS = [
  { valor: "newest", rotulo: "Mais recentes" },
  { valor: "spend_desc", rotulo: "Maior gasto" },
  { valor: "spend_asc", rotulo: "Menor gasto" }
];

const desdeDias = (dias) => {
  const data = new Date();
  data.setDate(data.getDate() - dias + 1);

  const pad = (n) => String(n).padStart(2, "0");

  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(
    data.getDate()
  )}`;
};

// Bolinha da situação: cor sozinha não identifica nada para quem não
// distingue as cores, então vem sempre com o rótulo ao lado.
const Situacao = ({ status }) => {
  const situacao = situacaoDe(status);

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs">
      <span className={cn("h-2 w-2 rounded-full", situacao.classe)} />
      <span className={situacao.texto}>{situacao.rotulo}</span>
    </span>
  );
};

// O CTR com o veredito ao lado. É a informação que falta no gerenciador: um
// número de porcentagem só vira decisão quando comparado com alguma coisa.
const Ctr = ({ valor, media }) => {
  const comparacao = compararCtr(valor, media);

  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {porcento(valor)}
      {comparacao === "acima" && (
        <span
          className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400"
          title={`Acima da média da conta (${porcento(media)})`}
        >
          <TrendingUp className="h-3 w-3" />
        </span>
      )}
      {comparacao === "abaixo" && (
        <span
          className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400"
          title={`Abaixo da média da conta (${porcento(media)})`}
        >
          <TrendingDown className="h-3 w-3" />
        </span>
      )}
    </span>
  );
};

// A linha de números de um nível qualquer da árvore.
const Numeros = ({ no, media, moeda }) => {
  const m = no.metrics || {};
  const resultado = resultadoDe(no);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">
        {dinheiro(m.spend, moeda)}
      </span>
      <span>{numero(m.impressions)} impressões</span>
      <Ctr valor={m.ctr} media={media} />

      {/* O que a campanha entregou, no nome do objetivo dela. */}
      {resultado.quantidade > 0 && (
        <span className="font-medium text-foreground">
          {numero(resultado.quantidade)} {resultado.rotulo}
          {resultado.custo !== null && (
            <span className="ml-1 font-normal text-muted-foreground">
              · {dinheiro(resultado.custo, moeda)} cada
            </span>
          )}
        </span>
      )}
    </div>
  );
};

const Anuncios = () => {
  const [campanhas, setCampanhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [abertas, setAbertas] = useState({});
  const [mexendo, setMexendo] = useState(null);
  const [editando, setEditando] = useState(null);
  const [criando, setCriando] = useState(false);

  const [periodo, setPeriodo] = useState(30);
  const [situacao, setSituacao] = useState("todas");
  const [ordem, setOrdem] = useState("newest");

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/zernio/ads", {
        params: {
          fromDate: desdeDias(periodo),
          sort: ordem,
          ...(situacao !== "todas" ? { status: situacao } : {})
        }
      });

      setCampanhas(data.campaigns || []);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, [periodo, situacao, ordem]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const resumo = useMemo(() => somar(campanhas), [campanhas]);
  const media = useMemo(() => ctrMedio(campanhas), [campanhas]);
  const maiorGasto = useMemo(
    () => Math.max(...campanhas.map((c) => c.metrics?.spend || 0), 1),
    [campanhas]
  );

  const alternar = (id) =>
    setAbertas((atuais) => ({ ...atuais, [id]: !atuais[id] }));

  const mudarStatus = async (no, nivel, id, novo) => {
    setMexendo(id);

    try {
      const { data } = await api.put(`/zernio/ads/${id}/status`, {
        nivel,
        status: novo,
        platform: no.platform
      });

      // A plataforma pula o que já terminou. Dizer quantos mudaram e quantos
      // ficaram evita a impressão de que o clique não funcionou.
      toast.success(
        data?.skipped
          ? `${data.updated || 0} atualizados, ${data.skipped} pulados.`
          : novo === "active"
            ? "Retomado."
            : "Pausado."
      );

      await carregar();
    } catch (err) {
      toastError(err);
    }

    setMexendo(null);
  };

  const duplicar = async (campanha) => {
    setMexendo(campanha.platformCampaignId);

    try {
      await api.post(
        `/zernio/ads/campaigns/${campanha.platformCampaignId}/duplicate`,
        {
          platform: campanha.platform,
          name: `${campanha.campaignName} — cópia`
        }
      );

      toast.success("Campanha duplicada. A cópia nasce pausada.");
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setMexendo(null);
  };

  return (
    <div className="space-y-4 p-4">
      <Cabecalho
        titulo="Anúncios"
        descricao="Campanhas das redes de mídia paga, com o custo do que cada uma entrega — conversa, lead ou venda, conforme o objetivo."
        acao={
          <Button onClick={() => setCriando(true)}>
            <Megaphone className="h-4 w-4" />
            Nova campanha
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-0.5 rounded-lg border p-0.5">
          {PERIODOS.map((item) => (
            <Button
              key={item.valor}
              variant={periodo === item.valor ? "secondary" : "ghost"}
              size="sm"
              className="h-7"
              onClick={() => setPeriodo(item.valor)}
            >
              {item.rotulo}
            </Button>
          ))}
        </div>

        <Select value={situacao} onValueChange={setSituacao}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SITUACOES_FILTRO.map((item) => (
              <SelectItem key={item.valor} value={item.valor}>
                {item.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={ordem} onValueChange={setOrdem}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORDENS.map((item) => (
              <SelectItem key={item.valor} value={item.valor}>
                {item.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* O resumo do período, com o custo por conversa em destaque: numa
          instalação de atendimento, é ele que diz se o anúncio compensa. */}
      {campanhas.length > 0 && (
        <div className="grid gap-3 rounded-xl border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-2xl font-semibold tabular-nums leading-none">
              {dinheiro(resumo.gasto, resumo.moeda)}
            </p>
            <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
              gasto no período
            </p>
          </div>
          <div>
            <p className="text-2xl font-semibold tabular-nums leading-none">
              {numero(resumo.conversas)}
            </p>
            <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
              conversas iniciadas
            </p>
          </div>
          <div>
            <p className="text-2xl font-semibold tabular-nums leading-none">
              {dinheiro(resumo.custoPorConversa, resumo.moeda)}
            </p>
            <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
              por conversa
            </p>
          </div>
          <div>
            <p className="text-2xl font-semibold tabular-nums leading-none">
              {porcento(resumo.ctr)}
            </p>
            <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
              CTR médio · {numero(resumo.impressoes)} impressões
            </p>
          </div>
        </div>
      )}

      {carregando ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : campanhas.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Megaphone className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-medium">Nenhuma campanha</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Conecte uma conta de anúncios no painel da Zernio, ou troque o
            período e a situação acima.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {campanhas.map((campanha) => {
            const id = campanha.platformCampaignId;
            const rede = identidadeDaRede(campanha.platform);
            const Glifo = rede.icone;
            const aberta = abertas[id];
            const problema = problemaDe(campanha);
            const ativa = campanha.status === "active";
            const gasto = campanha.metrics?.spend || 0;

            return (
              <div key={id} className="rounded-xl border bg-card">
                <div className="flex items-start gap-3 p-3">
                  <button
                    type="button"
                    onClick={() => alternar(id)}
                    className="mt-0.5 shrink-0 rounded p-0.5 hover:bg-accent"
                    aria-label={aberta ? "Recolher" : "Expandir"}
                    aria-expanded={aberta}
                  >
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform",
                        aberta && "rotate-90"
                      )}
                    />
                  </button>

                  <span
                    className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ backgroundColor: rede.corSelo }}
                    title={rede.rotulo}
                  >
                    <Glifo className="h-4 w-4" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium leading-tight">
                        {campanha.campaignName}
                      </p>
                      <Situacao status={campanha.status} />
                      <span className="text-xs text-muted-foreground">
                        {campanha.adCount} anúncios
                      </span>
                      {campanha.budget?.amount > 0 && (
                        <Badge variant="secondary">
                          {dinheiro(campanha.budget.amount, campanha.currency)}
                          {campanha.budget.type === "daily" ? "/dia" : " total"}
                        </Badge>
                      )}
                    </div>

                    {/* A barra dá a proporção do gasto entre campanhas — ler
                        oito valores em reais e ordenar de cabeça é o trabalho
                        que a tela deveria poupar. */}
                    <div className="mt-1.5 h-1 w-full max-w-xs overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{
                          width: `${Math.max((gasto / maiorGasto) * 100, gasto > 0 ? 2 : 0)}%`
                        }}
                      />
                    </div>

                    <div className="mt-1.5">
                      <Numeros
                        no={campanha}
                        media={media}
                        moeda={campanha.currency}
                      />
                    </div>

                    {problema && (
                      <p className="mt-1.5 inline-flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
                        <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" />
                        {problema}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={mexendo !== null}
                      onClick={() =>
                        mudarStatus(
                          campanha,
                          "campaign",
                          id,
                          ativa ? "paused" : "active"
                        )
                      }
                      aria-label={ativa ? "Pausar campanha" : "Retomar campanha"}
                      title={ativa ? "Pausar campanha" : "Retomar campanha"}
                    >
                      {mexendo === id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : ativa ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditando(campanha)}
                      aria-label="Editar campanha"
                      title="Editar nome e orçamento"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={mexendo !== null}
                      onClick={() => duplicar(campanha)}
                      aria-label="Duplicar campanha"
                      title="Duplicar campanha"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {aberta && (
                  <div className="space-y-2 border-t bg-muted/30 p-3 pl-10">
                    {(campanha.adSets || []).map((conjunto) => (
                      <div
                        key={conjunto.platformAdSetId}
                        className="rounded-lg border bg-card p-2.5"
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-medium">
                                {conjunto.adSetName}
                              </p>
                              <Situacao status={conjunto.status} />
                              <span className="text-xs text-muted-foreground">
                                {conjunto.adCount} anúncios
                              </span>
                            </div>
                            <div className="mt-1">
                              <Numeros
                                no={conjunto}
                                media={media}
                                moeda={campanha.currency}
                              />
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={mexendo !== null}
                            onClick={() =>
                              mudarStatus(
                                campanha,
                                "adset",
                                conjunto.platformAdSetId,
                                conjunto.status === "active"
                                  ? "paused"
                                  : "active"
                              )
                            }
                            aria-label={
                              conjunto.status === "active"
                                ? "Pausar conjunto"
                                : "Retomar conjunto"
                            }
                          >
                            {mexendo === conjunto.platformAdSetId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : conjunto.status === "active" ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        </div>

                        {(conjunto.ads || []).length > 0 && (
                          <div className="mt-2 space-y-1.5 border-t pt-2">
                            {conjunto.ads.map((anuncio) => {
                              const problemaAd = problemaDe(anuncio);

                              return (
                                <div
                                  key={anuncio.platformAdId}
                                  className="flex items-start gap-2"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="truncate text-xs font-medium">
                                        {anuncio.name}
                                      </p>
                                      <Situacao status={anuncio.status} />
                                    </div>
                                    <div className="mt-0.5">
                                      <Numeros
                                        no={anuncio}
                                        media={media}
                                        moeda={campanha.currency}
                                      />
                                    </div>
                                    {problemaAd && (
                                      <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                                        {problemaAd}
                                      </p>
                                    )}
                                  </div>

                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    disabled={mexendo !== null}
                                    onClick={() =>
                                      mudarStatus(
                                        campanha,
                                        "ad",
                                        anuncio.platformAdId,
                                        anuncio.status === "active"
                                          ? "paused"
                                          : "active"
                                      )
                                    }
                                    aria-label={
                                      anuncio.status === "active"
                                        ? "Pausar anúncio"
                                        : "Retomar anúncio"
                                    }
                                  >
                                    {mexendo === anuncio.platformAdId ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : anuncio.status === "active" ? (
                                      <Pause className="h-3.5 w-3.5" />
                                    ) : (
                                      <Play className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CampanhaModal
        open={criando || Boolean(editando)}
        campanha={editando}
        onClose={() => {
          setCriando(false);
          setEditando(null);
        }}
        onSalvo={carregar}
      />
    </div>
  );
};

export default Anuncios;
